import type { ButtonHTMLAttributes, CSSProperties, ReactNode } from 'react';
import { create } from 'zustand';
import { de } from '../i18n/de';
import { ProjectName } from './ProjectName';

/** Eingeklappte Leisten je Schlüssel – nur im Arbeitsspeicher: beim Öffnen der Seite ist jede Leiste ausgeklappt. */
const useRails = create<{ collapsed: Record<string, boolean>; toggle: (key: string) => void }>((set) => ({
  collapsed: {},
  toggle: (key) => set((s) => ({ collapsed: { ...s.collapsed, [key]: !s.collapsed[key] } })),
}));

/** Eingeklappt ja/nein (je Leiste): gilt beim Wechsel der Modi weiter; nach dem Neuladen ist die Leiste wieder ausgeklappt. */
export function useRailCollapsed(key: string): [boolean, () => void] {
  const collapsed = useRails((s) => s.collapsed[key] ?? false);
  const toggle = useRails((s) => s.toggle);
  return [collapsed, () => toggle(key)];
}

/** Symbol (SVG oder PNG mit Transparenz) in der Farbe der Oberfläche – als Maske, damit es im hellen wie im dunklen Modus passt. */
export function MaskIcon({ url, size = 22 }: { url: string; size?: number }) {
  const mask = `url("${url}") center / contain no-repeat`;
  return <span className="mask-icon" style={{ width: size, height: size, mask, WebkitMask: mask } as CSSProperties} data-icon={url} aria-hidden="true" />;
}

interface RailProps {
  title: string;
  collapsed: boolean;
  onToggle: () => void;
  testId: string;
  children: ReactNode;
  /** unter den Einträgen (nur ausgeklappt) */
  footer?: ReactNode;
  /** ganz unten in der Leiste, auch eingeklappt (z. B. ein weiterer Eintrag wie „Importieren“) */
  bottom?: ReactNode;
}

/**
 * Seitenleiste links (Bibliothek: Bereiche, Handlung: Werkzeuge): ganz oben der Name der Geschichte und rechts daneben ein Pfeil zum
 * Einklappen, darunter Titel und Einträge – eingeklappt bleiben nur die Symbole (der Name steht im Hinweis).
 */
export function SideRail({ title, collapsed, onToggle, testId, children, footer, bottom }: RailProps) {
  const toggleLabel = collapsed ? de.rail.expand : de.rail.collapse;
  return (
    <nav className={`side-rail${collapsed ? ' collapsed' : ''}`} aria-label={title} data-testid={testId}>
      <div className="side-rail-top">
        <ProjectName />
        <button type="button" className="side-rail-toggle" onClick={onToggle} aria-expanded={!collapsed} aria-label={toggleLabel} title={toggleLabel} data-testid={`${testId}-toggle`}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M13 6l-6 6 6 6" />
            <path d="M19 6l-6 6 6 6" />
          </svg>
        </button>
      </div>
      <div className="side-rail-head">
        <span className="side-rail-title">{title}</span>
      </div>
      <div className="side-rail-items">{children}</div>
      {footer && !collapsed && <div className="side-rail-foot">{footer}</div>}
      {bottom && <div className="side-rail-bottom">{bottom}</div>}
    </nav>
  );
}

type ItemProps = {
  icon: ReactNode;
  label: string;
  count?: number | null;
  active?: boolean;
  disabled?: boolean;
  /** feine Trennlinie davor (neue Gruppe) */
  separated?: boolean;
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'>;

/** Eintrag der Seitenleiste: Symbol, Name und ggf. Anzahl; eingeklappt nur das Symbol. */
export function RailItem({ icon, label, count, active, disabled, separated, className, title, ...rest }: ItemProps) {
  return (
    <button
      type="button"
      className={`rail-item${active ? ' active' : ''}${disabled ? ' disabled' : ''}${separated ? ' separated' : ''}${className ? ` ${className}` : ''}`}
      aria-label={label}
      aria-disabled={disabled || undefined}
      title={title ?? (count === undefined || count === null ? label : `${label} (${count})`)}
      {...rest}
    >
      <span className="rail-icon">{icon}</span>
      <span className="rail-label">{label}</span>
      {count !== undefined && count !== null && <span className="rail-count">{count}</span>}
    </button>
  );
}
